import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, type BillingInfo, type BillingUsage, type BillingInvoice } from '../api/client';
import {
  CreditCard,
  CheckCircle,
  ArrowSquareOut,
  Receipt,
  Gauge,
} from '@phosphor-icons/react';

const PLAN_FEATURES: Record<string, { name: string; price: string; features: string[] }> = {
  free: {
    name: 'Free',
    price: '$0/mo',
    features: [
      '10,000 calls/month',
      '1 workspace',
      '3 members',
      '5 API keys',
      '7-day audit retention',
      'Basic DLP & Policy',
    ],
  },
  pro: {
    name: 'Pro',
    price: '$99/mo',
    features: [
      '100,000 calls/month',
      '5 workspaces',
      '10 members',
      '25 API keys',
      '30-day audit retention',
      'Prompt injection detection',
      'Anomaly detection',
      'Custom policies',
    ],
  },
  business: {
    name: 'Business',
    price: '$499/mo',
    features: [
      '1,000,000 calls/month',
      '20 workspaces',
      '50 members',
      '100 API keys',
      '90-day audit retention',
      'SSO',
      'Advanced DLP',
      'All Pro features',
    ],
  },
  enterprise: {
    name: 'Enterprise',
    price: 'Custom',
    features: [
      'Unlimited calls',
      'Unlimited workspaces',
      'Unlimited members',
      'Unlimited API keys',
      '365-day audit retention',
      'Dedicated support',
      'Custom integrations',
      'All Business features',
    ],
  },
};

function StatusBadge({ status }: { status?: string }) {
  const s = (status || '').toLowerCase();
  let bg = 'bg-[rgba(52,211,153,0.12)]';
  let fg = 'text-[var(--accent-teal)]';
  if (s === 'past_due') {
    bg = 'bg-[rgba(251,191,36,0.12)]';
    fg = 'text-[var(--color-warning)]';
  } else if (s === 'canceled' || s === 'incomplete') {
    bg = 'bg-[rgba(248,113,113,0.12)]';
    fg = 'text-[var(--color-error)]';
  } else if (s === 'trialing') {
    bg = 'bg-[rgba(139,92,246,0.12)]';
    fg = 'text-[var(--accent-violet)]';
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${bg} ${fg}`}>
      {status || 'active'}
    </span>
  );
}

export function Billing() {
  const { currentWorkspaceId: workspaceId } = useAuth();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [usage, setUsage] = useState<BillingUsage | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    async function loadBilling() {
      try {
        const [billingData, usageData, invoiceData] = await Promise.all([
          api.getBilling(workspaceId!).catch(() => null),
          api.getBillingUsage(workspaceId!).catch(() => null),
          api.getBillingInvoices(workspaceId!).catch(() => ({ invoices: [] as BillingInvoice[] })),
        ]);
        if (billingData) setBilling(billingData);
        if (usageData) setUsage(usageData);
        setInvoices(invoiceData.invoices);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load billing data.');
      } finally {
        setLoading(false);
      }
    }

    loadBilling();
  }, [workspaceId]);

  async function handleCheckout(plan: string) {
    if (!workspaceId) return;
    setCheckoutLoading(plan);
    try {
      const { url } = await api.createCheckout(workspaceId, plan);
      if (url) window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout.');
    } finally {
      setCheckoutLoading(null);
    }
  }

  async function handlePortal() {
    if (!workspaceId) return;
    setPortalLoading(true);
    try {
      const { url } = await api.createPortalSession(workspaceId);
      if (url) window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner-gradient h-8 w-8" />
      </div>
    );
  }

  if (!workspaceId) {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-8 text-center">
        <h2 className="text-lg font-semibold text-text-primary">No workspace selected</h2>
        <p className="mt-2 text-sm text-text-secondary">Select a workspace to manage billing.</p>
      </div>
    );
  }

  const currentPlan = billing?.plan || 'free';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Billing</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Manage your plan, usage, and payment methods
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {/* Current Plan + Usage */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Current Plan Card */}
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={20} weight="duotone" className="text-text-tertiary" />
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-text-tertiary">
              Current Plan
            </h3>
          </div>
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-2xl font-bold text-text-primary">
              {PLAN_FEATURES[currentPlan]?.name || currentPlan}
            </span>
            <span className="text-lg text-text-secondary">
              {PLAN_FEATURES[currentPlan]?.price}
            </span>
          </div>
          {billing?.subscription && (
            <div className="flex items-center gap-3 mt-3">
              <StatusBadge status={billing.subscription.status} />
              {billing.subscription.current_period_end && (
                <span className="text-xs text-text-tertiary">
                  Renews {new Date(billing.subscription.current_period_end).toLocaleDateString()}
                </span>
              )}
              {billing.subscription.cancel_at_period_end && (
                <span className="text-xs text-[var(--color-warning)]">
                  Cancels at period end
                </span>
              )}
            </div>
          )}
          {billing?.subscription?.stripe_customer_id && (
            <button
              onClick={handlePortal}
              disabled={portalLoading}
              className="mt-4 flex items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm text-text-secondary hover:bg-[var(--surface-2)] transition-colors disabled:opacity-50"
            >
              <ArrowSquareOut size={16} weight="duotone" />
              {portalLoading ? 'Opening...' : 'Manage Payment'}
            </button>
          )}
        </div>

        {/* Usage Card */}
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Gauge size={20} weight="duotone" className="text-text-tertiary" />
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-text-tertiary">
              Monthly Usage
            </h3>
          </div>
          {usage && (
            <>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-2xl font-bold text-text-primary tabular-nums">
                  {usage.calls_this_month.toLocaleString()}
                </span>
                <span className="text-sm text-text-secondary">
                  / {usage.calls_limit === Infinity ? 'Unlimited' : usage.calls_limit.toLocaleString()} calls
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-[var(--surface-2)] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(usage.usage_percent, 100)}%`,
                    background: usage.usage_percent > 90
                      ? 'var(--color-error)'
                      : usage.usage_percent > 70
                        ? 'var(--color-warning)'
                        : 'var(--gradient-accent)',
                  }}
                />
              </div>
              <p className="mt-2 text-xs text-text-tertiary">
                {usage.usage_percent}% of monthly limit used
              </p>
            </>
          )}
        </div>
      </div>

      {/* Plan Comparison Grid */}
      <div>
        <h3 className="text-[11px] font-medium uppercase tracking-wider text-text-tertiary mb-4">
          Available Plans
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(PLAN_FEATURES).map(([key, plan]) => {
            const isCurrent = key === currentPlan;
            return (
              <div
                key={key}
                className={`rounded-xl border p-5 transition-colors ${
                  isCurrent
                    ? 'border-[var(--accent-teal)] bg-[rgba(6,182,164,0.04)]'
                    : 'border-[var(--border)] bg-[var(--surface-1)]'
                }`}
              >
                <h4 className="text-base font-semibold text-text-primary">{plan.name}</h4>
                <p className="text-xl font-bold text-text-primary mt-1">{plan.price}</p>
                <ul className="mt-4 space-y-2">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2 text-xs text-text-secondary">
                      <CheckCircle
                        size={14}
                        weight="fill"
                        className={isCurrent ? 'text-[var(--accent-teal)] shrink-0 mt-0.5' : 'text-text-tertiary shrink-0 mt-0.5'}
                      />
                      {feat}
                    </li>
                  ))}
                </ul>
                <div className="mt-5">
                  {isCurrent ? (
                    <span className="inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium text-[var(--accent-teal)] bg-[rgba(6,182,164,0.12)]">
                      Current Plan
                    </span>
                  ) : key === 'enterprise' ? (
                    <span className="inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium text-text-secondary border border-[var(--border)]">
                      Contact Sales
                    </span>
                  ) : key === 'free' ? (
                    null
                  ) : (
                    <button
                      onClick={() => handleCheckout(key)}
                      disabled={!!checkoutLoading}
                      className="rounded-lg px-3 py-1.5 text-sm font-medium text-white transition-colors disabled:opacity-50"
                      style={{ background: 'var(--gradient-accent)' }}
                    >
                      {checkoutLoading === key ? 'Loading...' : `Upgrade to ${plan.name}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoices */}
      {invoices.length > 0 && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Receipt size={20} weight="duotone" className="text-text-tertiary" />
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-text-tertiary">
              Recent Invoices
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--surface-2)]">
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-text-tertiary">Invoice</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-text-tertiary">Status</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-text-tertiary">Amount</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-text-tertiary">Date</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-text-tertiary"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-[var(--border-subtle)] last:border-0">
                    <td className="px-4 py-2.5 font-mono text-xs text-text-secondary">
                      {inv.number || inv.id.slice(0, 20)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={inv.status || undefined} />
                    </td>
                    <td className="px-4 py-2.5 text-text-primary tabular-nums">
                      ${((inv.amount_paid || inv.amount_due) / 100).toFixed(2)} {inv.currency?.toUpperCase()}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-text-secondary">
                      {inv.created ? new Date(inv.created).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-2.5">
                      {inv.hosted_invoice_url && (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[var(--accent-teal)] hover:underline"
                        >
                          View
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
